"use client";

import { c as _c } from "react/compiler-runtime";
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useId, useRef, useState, useTransition } from "react";
import { cn } from "@local/utils/src/cn";
import { Flag } from "./flags";
import { marketsData } from "@local/offer/src/lib/const/marketsData";
import { isRetiredMarket } from "@local/i18n/src/retired-markets";
import { useOutsideClickCapture } from "@local/utils/src/useOutsideClickCapture";
import { CheckIcon } from "@local/ui/src/components/icons/Check";
import Cookies from "js-cookie";
import { I18n } from "@local/offer/src/lib/i18n";
import { useRouter } from "next/navigation";
import type { Route } from "next";
import { trackAction } from "@local/tracking/src/trackActions";
import { ArrowDownIcon } from "@local/ui/src/components/ui/icons";
import { LoadingSpinner } from "@local/ui/src/LoadingSpinner";
import { getT } from "@local/i18n/src/t";
import { composeMarketSlug, getLanguageForMarket, defaultLanguageFor, languagesForMarket, marketBaseOf, parseMarketSlug } from "@local/i18n/src/languages";
import { nativeLanguageName } from "@local/i18n/src/languageNames";
import { setLangPref } from "@local/i18n/src/langCookie";
import { a11y } from "@local/i18n/src/catalogs/a11y";
import { marketSelector } from "@local/i18n/src/catalogs/marketSelector";
import { countryNames, localizedCountryName } from "@local/i18n/src/catalogs/countryNames";
interface Props {
  selected: string;
  lightBackground?: boolean;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
}

// Retired markets stay in `marketsData` (historical orders still resolve their
// currency through it) but are never offered as a destination — DEV-10186.
const MARKET_OPTIONS = marketsData.filter(c => !isRetiredMarket(c.market)).map(c => ({
  value: c.market,
  label: c.name,
  currencySymbol: c.currencySymbol
}));
const FlagWrapper = t0 => {
  const $ = _c(2);
  const {
    country
  } = t0;
  let t1;
  if ($[0] !== country) {
    t1 = <div className="flex aspect-square items-center justify-center rounded-full bg-gray-200 p-1.5"><div className="flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-full"><Flag country={country} className="h-full w-full object-cover" width={16} height={16} /></div></div>;
    $[0] = country;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const LanguageMarketSelector = t0 => {
  const $ = _c(49);
  const {
    selected,
    lightBackground: t1,
    className,
    wrapperClassName: t2,
    disabled: t3
  } = t0;
  const lightBackground = t1 === undefined ? false : t1;
  const wrapperClassName = t2 === undefined ? "" : t2;
  const disabled = t3 === undefined ? false : t3;
  const {
    market: baseMarket,
    language: currentLang
  } = parseMarketSlug(selected.toLowerCase());
  const [pendingMarket, setPendingMarket] = useState(null);
  const displayMarket = pendingMarket ?? baseMarket;
  const langs = languagesForMarket(displayMarket);
  const isMulti = langs.length > 1;
  const displayLang = pendingMarket ? defaultLanguageFor(pendingMarket) : currentLang;
  const [regionOpen, setRegionOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const uniqueId = useId();
  const wrapperRef = useRef(null);
  const regionBtnRef = useRef(null);
  const langBtnRef = useRef(null);
  const regionListRef = useRef(null);
  const langListRef = useRef(null);
  const {
    push
  } = useRouter();
  const enabledOptions = _temp;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (e, container_0) => {
      const options = enabledOptions(container_0);
      if (options.length === 0) {
        return;
      }
      const currentIndex = options.indexOf(document.activeElement as HTMLButtonElement);
      const last = options.length - 1;
      let nextIndex;
      bb31: switch (e.key) {
        case "ArrowDown":
          {
            nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
            break bb31;
          }
        case "ArrowUp":
          {
            nextIndex = currentIndex <= 0 ? last : currentIndex - 1;
            break bb31;
          }
        case "Home":
          {
            nextIndex = 0;
            break bb31;
          }
        case "End":
          {
            nextIndex = last;
            break bb31;
          }
        default:
          {
            return;
          }
      }
      e.preventDefault();
      options[nextIndex]?.focus();
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const handleListboxKeyDown = t4;
  let t5;
  let t6;
  if ($[1] !== regionOpen) {
    t5 = () => {
      if (!regionOpen) {
        return;
      }
      enabledOptions(regionListRef.current)[0]?.focus();
    };
    t6 = [regionOpen];
    $[1] = regionOpen;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[4] !== langOpen) {
    t7 = () => {
      if (!langOpen) {
        return;
      }
      enabledOptions(langListRef.current)[0]?.focus();
    };
    t8 = [langOpen];
    $[4] = langOpen;
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  useEffect(t7, t8);
  const t = getT(getLanguageForMarket(selected), {
    a11y,
    countryNames,
    marketSelector
  });
  let t9;
  if ($[7] !== push || $[8] !== selected) {
    t9 = targetSlug => {
      Cookies.set("override_store", marketBaseOf(targetSlug));
      const url = I18n.buildMarketUrl(window.location.href, selected.toLowerCase(), targetSlug.toLowerCase());
      startTransition(() => push(url.href as Route));
    };
    $[7] = push;
    $[8] = selected;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const navigateTo = t9;
  let t10;
  if ($[10] !== navigateTo || $[11] !== pendingMarket) {
    t10 = () => {
      if (!pendingMarket) {
        return;
      }
      const market = pendingMarket;
      setPendingMarket(null);
      setLangOpen(false);
      navigateTo(market);
    };
    $[10] = navigateTo;
    $[11] = pendingMarket;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const commitPending = t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setPendingMarket(null);
      setLangOpen(false);
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const cancelPending = t11;
  let t12;
  if ($[14] !== commitPending) {
    t12 = () => {
      setRegionOpen(false);
      setLangOpen(false);
      commitPending();
    };
    $[14] = commitPending;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  useOutsideClickCapture(wrapperRef, t12);
  let t13;
  let t14;
  if ($[16] !== commitPending || $[17] !== langOpen || $[18] !== regionOpen) {
    t13 = () => {
      if (!regionOpen && !langOpen) {
        return;
      }
      const onEsc = e_0 => {
        if (e_0.key !== "Escape") {
          return;
        }
        if (langOpen) {
          setLangOpen(false);
          langBtnRef.current?.focus();
          commitPending();
        }
        if (regionOpen) {
          setRegionOpen(false);
          regionBtnRef.current?.focus();
        }
      };
      document.addEventListener("keydown", onEsc);
      return () => document.removeEventListener("keydown", onEsc);
    };
    t14 = [regionOpen, langOpen, commitPending];
    $[16] = commitPending;
    $[17] = langOpen;
    $[18] = regionOpen;
    $[19] = t13;
    $[20] = t14;
  } else {
    t13 = $[19];
    t14 = $[20];
  }
  useEffect(t13, t14);
  const selectedCountry = MARKET_OPTIONS.find(m => m.value === displayMarket);
  const regionLabel = selectedCountry ? `${localizedCountryName(t, selectedCountry.value, selectedCountry.label)} (${selectedCountry.currencySymbol})` : "";
  let t15;
  if ($[21] !== navigateTo || $[22] !== selected) {
    t15 = marketValue => {
      const market_0 = marketValue.toLowerCase();
      const base = marketBaseOf(market_0);
      const country = marketsData.find(c => c.market === base);
      if (!country) {
        console.error(`User tried to change to an invalid country: ${marketValue}`);
        return;
      }
      trackAction("market_selector_change", {
        from: selected.toLowerCase(),
        to: market_0
      });
      if (languagesForMarket(base).length > 1) {
        setPendingMarket(base);
        setRegionOpen(false);
        setLangOpen(true);
        return;
      }
      setPendingMarket(null);
      navigateTo(market_0);
    };
    $[21] = navigateTo;
    $[22] = selected;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const handleRegionChange = t15;
  const t16 = !isMulti && "rounded-full border-2 border-gray-200 focus:border-sky-500";
  const t17 = isMulti && "rounded-l-full";
  const t18 = disabled && "cursor-not-allowed opacity-60";
  let t19;
  if ($[24] !== className || $[25] !== t16 || $[26] !== t17 || $[27] !== t18) {
    t19 = cn("relative cursor-pointer bg-none py-2 pr-12 pl-4 text-left transition-all duration-200 focus:ring-2 focus:ring-sky-500 focus:outline-none", t16, t17, t18, className);
    $[24] = className;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== disabled || $[30] !== regionOpen) {
    t20 = () => {
      if (!disabled) {
        cancelPending();
        setRegionOpen(!regionOpen);
      }
    };
    $[29] = disabled;
    $[30] = regionOpen;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = lightBackground ? "text-black" : "text-white";
  let t22;
  if ($[32] !== t21) {
    t22 = cn("font-body font-medium", t21);
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== regionLabel || $[35] !== t22) {
    t23 = <span className={t22}>{regionLabel}</span>;
    $[34] = regionLabel;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const t24 = regionOpen ? "text-sky-500" : "text-gray-200";
  let t25;
  if ($[37] !== t24) {
    t25 = cn("transition-colors", t24);
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t25) {
    t26 = <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"><ArrowDownIcon className={t25} aria-hidden="true" /></span>;
    $[39] = t25;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  const regionButton = <button ref={regionBtnRef} type="button" className={t19} disabled={disabled} aria-haspopup="listbox" aria-expanded={regionOpen} aria-controls={`region-listbox-${uniqueId}`} aria-label={isMulti ? t("marketSelector.regionAria", {
    value: regionLabel
  }) : t("a11y.selectCountry")} onClick={t20}><span className="flex items-center gap-3">{selectedCountry && <FlagWrapper country={selectedCountry.value} />}{t23}</span>{t26}</button>;
  const regionListbox = regionOpen && <div ref={regionListRef} id={`region-listbox-${uniqueId}`} role="listbox" tabIndex={-1} aria-label={t("a11y.selectCountry")} onKeyDown={e_1 => handleListboxKeyDown(e_1, regionListRef.current)} className="absolute bottom-full left-0 z-50 mb-1 max-h-60 w-max overflow-auto border border-gray-200 bg-white shadow-lg"><ul className="py-1">{MARKET_OPTIONS.map(market_1 => {
        const isSelected = selectedCountry?.value === market_1.value;
        return <li key={`market-option-${market_1.value}-${uniqueId}`}><button type="button" role="option" aria-selected={isSelected} disabled={isSelected} className={cn("relative flex w-full cursor-pointer items-center gap-3 py-3 pr-12 pl-4 text-sm transition-colors duration-150 select-none hover:bg-gray-100", isSelected ? "cursor-not-allowed bg-sky-50 text-sky-900" : "text-gray-900")} onClick={() => {
            if (!isSelected) {
              handleRegionChange(market_1.value);
              setRegionOpen(false);
            }
          }}><FlagWrapper country={market_1.value} /><span className="font-body flex-1 text-start font-medium">{localizedCountryName(t, market_1.value, market_1.label)} ({market_1.currencySymbol})</span>{isSelected && <span className="absolute right-4 flex items-center text-sky-600"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span>}</button></li>;
      })}</ul></div>;
  const languageHalf = isMulti && <><span aria-hidden="true" className="mx-1 w-px self-stretch bg-gray-300" /><div className="relative"><button ref={langBtnRef} type="button" className={cn("flex min-h-[44px] cursor-pointer items-center gap-2 rounded-r-full px-4 py-2 transition-colors hover:bg-gray-100/10 focus:ring-2 focus:ring-sky-500 focus:outline-none", disabled && "cursor-not-allowed opacity-60")} disabled={disabled} aria-haspopup="listbox" aria-expanded={langOpen} aria-controls={`lang-listbox-${uniqueId}`} aria-label={t("marketSelector.languageAria", {
        value: nativeLanguageName(displayLang)
      })} onClick={() => {
        if (!disabled) {
          setRegionOpen(false);
          if (langOpen) {
            setLangOpen(false);
            commitPending();
          } else {
            setLangOpen(true);
          }
        }
      }}><span className={cn("font-body font-medium", lightBackground ? "text-black" : "text-white")}>{nativeLanguageName(displayLang)}</span><ArrowDownIcon className={cn("transition-all", langOpen && "rotate-180", langOpen ? "text-sky-500" : "text-gray-200")} aria-hidden="true" /></button>{langOpen && <div ref={langListRef} id={`lang-listbox-${uniqueId}`} role="listbox" tabIndex={-1} aria-label={t("marketSelector.chooseLanguage")} onKeyDown={e_2 => handleListboxKeyDown(e_2, langListRef.current)} className="absolute bottom-full left-0 z-50 mb-1 max-h-60 w-max overflow-auto border border-gray-200 bg-white shadow-lg"><ul className="py-1">{langs.map(lang => {
            const isSel = lang === displayLang;
            const isLocked = isSel && !pendingMarket;
            return <li key={`lang-${lang}-${uniqueId}`}><button type="button" role="option" aria-selected={isSel} disabled={isLocked} className={cn("relative flex w-full cursor-pointer items-center gap-3 py-3 pr-12 pl-4 text-sm select-none hover:bg-gray-100", isLocked ? "cursor-not-allowed bg-sky-50 text-sky-900" : isSel ? "bg-sky-50 text-sky-900" : "text-gray-900")} onClick={() => {
                if (isLocked) {
                  return;
                }
                setLangPref(displayMarket, lang);
                trackAction("language_selector_change", {
                  from: currentLang,
                  to: lang
                });
                setPendingMarket(null);
                setLangOpen(false);
                navigateTo(composeMarketSlug(displayMarket, lang));
              }}><span className="font-body flex-1 text-start font-medium">{nativeLanguageName(lang)}</span>{isSel && <span className="absolute right-4 flex items-center text-sky-600"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span>}</button></li>;
          })}</ul></div>}</div></>;
  let t27;
  if ($[41] !== wrapperClassName) {
    t27 = cn("relative my-4 w-fit", wrapperClassName);
    $[41] = wrapperClassName;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  const t28 = isMulti ? <div role="group" aria-label={t("marketSelector.regionAndLanguage")} className="flex items-center rounded-full border-2 border-gray-200"><div className="relative">{regionButton}{regionListbox}</div>{languageHalf}</div> : <>{regionButton}{regionListbox}</>;
  let t29;
  if ($[43] !== isPending) {
    t29 = isPending && <div className="absolute inset-0 z-50 flex items-center justify-center rounded-full bg-white/80"><LoadingSpinner className="-mt-3 h-5 w-5" /></div>;
    $[43] = isPending;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== t27 || $[46] !== t28 || $[47] !== t29) {
    t30 = <div className={t27} ref={wrapperRef}>{t28}{t29}</div>;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  return t30;
};
function _temp(container) {
  return container ? Array.from(container.querySelectorAll("[role=\"option\"]:not([disabled])")) : [];
}